import { StatProps } from 'components/WorkoutCell/types';
import {
  DistanceUnitShort,
  numberWithCommas,
  PublicWorkout,
  roundToOneDecimal,
  roundToTwoDecimal,
  roundToWholeNumber,
  secondsToWordFormat,
  secondsToWordFormatMinutes,
  WeightUnit,
  workoutDistanceMeters,
  workoutDurationSeconds,
  WorkoutExercise,
  WorkoutExerciseSet,
  workoutReps,
} from 'hevy-shared';
import { userDistance, userShortDistance, userWeight } from 'utils/units';

interface Preferences {
  weightUnit: WeightUnit;
  distanceUnitShort: DistanceUnitShort;
}

export const calculateSecondColumnHeader = (e: WorkoutExercise): string => {
  switch (e.exercise_type) {
    case 'weight_reps':
    case 'bodyweight_assisted_reps':
    case 'bodyweight_reps':
      return 'WEIGHT & REPS';
    case 'reps_only':
      return 'REPS';
    case 'distance_duration':
      return 'DISTANCE & TIME';
    case 'duration':
      return 'TIME';
    case 'short_distance_weight':
      return 'WEIGHT & DISTANCE';
    case 'weight_duration':
      return 'WEIGHT & DURATION';
    case 'floors_duration':
      return 'FLOORS & DURATION';
    case 'steps_duration':
      return 'STEPS & DURATION';
  }
};

export const calculateSetValue = (
  e: WorkoutExercise,
  s: WorkoutExerciseSet,
  p: Preferences,
): string => {
  switch (e.exercise_type) {
    case 'weight_reps':
    case 'bodyweight_assisted_reps':
    case 'bodyweight_reps': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      return `${roundToTwoDecimal(weight)}${p.weightUnit} x ${s.reps} reps`;
    }

    case 'reps_only': {
      return `${s.reps ?? 0} reps`;
    }

    case 'distance_duration': {
      const distance = roundToOneDecimal(userDistance(s.distance_meters ?? 0, p.distanceUnitShort));
      const time = secondsToWordFormat(s.duration_seconds ?? 0);
      return `${distance}${p.distanceUnitShort} - ${time}`;
    }

    case 'duration': {
      return `${secondsToWordFormat(s.duration_seconds ?? 0)}`;
    }
    case 'short_distance_weight': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      const shortDistance = userShortDistance(s.distance_meters ?? 0);
      const distanceUnits = p.distanceUnitShort === 'km' ? 'm' : 'yrd';
      return `${roundToTwoDecimal(weight)}${p.weightUnit} - ${shortDistance}${distanceUnits}`;
    }
    case 'weight_duration': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      const duration = `${secondsToWordFormat(s.duration_seconds ?? 0)}`;
      return `${roundToTwoDecimal(weight)}${p.weightUnit} x ${duration}`;
    }
    case 'floors_duration': {
      const floors = roundToWholeNumber(s.custom_metric ?? 0);
      const duration = `${secondsToWordFormat(s.duration_seconds ?? 0)}`;
      return `${floors} floors - ${duration}`;
    }
    case 'steps_duration': {
      const steps = roundToWholeNumber(s.custom_metric ?? 0);
      const duration = `${secondsToWordFormat(s.duration_seconds ?? 0)}`;
      return `${steps} steps - ${duration}`;
    }
  }
};

export const calculatePublicWorkoutStats = (workout: PublicWorkout): StatProps[] => {
  const { weight_unit, distance_unit } = workout;
  const volumeKg = workout.estimated_volume_kg;
  const durationSeconds = workoutDurationSeconds(workout);
  const reps = workoutReps(workout);
  const distanceMeters = workoutDistanceMeters(workout);

  const stats: StatProps[] = [
    {
      title: 'Duration',
      value: secondsToWordFormatMinutes(durationSeconds),
      isPr: false,
    },
  ];

  const userVolume = userWeight(volumeKg, weight_unit);
  const distance = userDistance(distanceMeters, distance_unit);

  if (volumeKg > reps) {
    stats.push({
      title: 'Volume',
      value: `${numberWithCommas(roundToWholeNumber(userVolume))} ${weight_unit}`,
      isPr: false,
    });
  } else if (reps > distanceMeters) {
    stats.push({ title: 'Reps', value: `${reps}`, isPr: false });
  } else if (distance > 0) {
    stats.push({
      title: 'Distance',
      value: `${roundToTwoDecimal(distance)} ${distance_unit}`,
      isPr: false,
    });
  }

  return stats;
};
